import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PhoneIcon, KeyIcon, ArrowTopRightOnSquareIcon, ShoppingBagIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';

interface LoginProps {
  onShowSignup?: () => void;
}

const Login: React.FC<LoginProps> = ({ onShowSignup }) => {
  const [mobileNumber, setMobileNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [error, setError] = useState('');
  const [hasAcceptedPolicies, setHasAcceptedPolicies] = useState(false);
  const [policyError, setPolicyError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const [resetMobile, setResetMobile] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [showResetPasswordField, setShowResetPasswordField] = useState(false);
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');
  const [resetResendTimer, setResetResendTimer] = useState(0);
  const otpAbortControllerRef = useRef<AbortController | null>(null);
  const { login } = useAuth();
  const location = useLocation();

  // Format timer as MM:SS
  const formatTimer = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const isDemoMode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('demo') === 'true';
  }, [location.search]);

  // Demo mode credentials from environment variables
  const demoMobile = useMemo(() => {
    return import.meta.env.VITE_DEMO_MOBILE?.trim() || '';
  }, []);

  const demoMobileOtp = useMemo(() => {
    return import.meta.env.VITE_DEMO_MOBILEOTP?.trim() || '';
  }, []);

  const demoAccounts = useMemo(() => {
    const normalize = (value?: string) => (value ? value.trim() : '');

    const buildAccount = ({
      id,
      planName,
      defaultUsername,
      defaultPassword,
      envUsername,
      envPassword,
      envMobile,
    }: {
      id: 'basic' | 'pro';
      planName: string;
      defaultUsername: string;
      defaultPassword: string;
      envUsername?: string;
      envPassword?: string;
      envMobile?: string;
    }) => {
      const usernameSource = envUsername || envMobile || defaultUsername;
      const passwordSource = envPassword || defaultPassword;

      return {
        id,
        planName,
        username: normalize(usernameSource),
        password: normalize(passwordSource),
        mobile: normalize(envMobile),
        isConfigured: Boolean(normalize(envUsername || envMobile) && normalize(envPassword)),
        defaultUsername,
      };
    };

    return [
      buildAccount({
        id: 'basic',
        planName: 'Basic Plan',
        defaultUsername: 'basic-demo@shopzee.app',
        defaultPassword: 'BasicDemo@123',
        envUsername: import.meta.env.VITE_DEMO_BASIC_USERNAME,
        envPassword: import.meta.env.VITE_DEMO_BASIC_PASSWORD,
        envMobile: import.meta.env.VITE_DEMO_BASIC_MOBILE,
      }),
      buildAccount({
        id: 'pro',
        planName: 'Pro Plan',
        defaultUsername: 'pro-demo@shopzee.app',
        defaultPassword: 'ProDemo@123',
        envUsername: import.meta.env.VITE_DEMO_PRO_USERNAME,
        envPassword: import.meta.env.VITE_DEMO_PRO_PASSWORD,
        envMobile: import.meta.env.VITE_DEMO_PRO_MOBILE,
      }),
    ];
  }, []);

  const basicDemoAccount = useMemo(
    () => demoAccounts.find((account) => account.id === 'basic'),
    [demoAccounts]
  );

  // Auto-fill demo mobile and OTP when in demo mode
  useEffect(() => {
    if (isDemoMode && demoMobile && demoMobileOtp) {
      setMobileNumber(demoMobile);
      setOtp(demoMobileOtp);
      setIsOtpSent(true);
    } else if (isDemoMode && basicDemoAccount && basicDemoAccount.mobile && basicDemoAccount.password) {
      setMobileNumber(basicDemoAccount.mobile);
    }
  }, [isDemoMode, demoMobile, demoMobileOtp, basicDemoAccount]);

  // Track conversion on login page view (not in demo mode)
  useEffect(() => {
    if (!isDemoMode && typeof window !== 'undefined') {
      if (typeof (window as any).gtag === 'function') {
        (window as any).gtag('event', 'conversion', {
          'send_to': 'AW-17751140940/U-ilCKuN4cQbEMzUs5BC'
        });
      } else {
        const checkGtag = setInterval(() => {
          if (typeof (window as any).gtag === 'function') {
            (window as any).gtag('event', 'conversion', {
              'send_to': 'AW-17751140940/U-ilCKuN4cQbEMzUs5BC'
            });
            clearInterval(checkGtag);
          }
        }, 100);

        setTimeout(() => {
          clearInterval(checkGtag);
        }, 5000);
      }
    }
  }, [isDemoMode]);


  const handleDemoShopClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const url = 'https://demo.menunix.com';

    const callback = () => {
      window.open(url, '_blank', 'noopener,noreferrer');
    };
    if (Array.isArray((window as any).dataLayer)) {
      (window as any).dataLayer.push({
        event: "website_demo_starts",
      });
    }

    if (typeof (window as any).gtag === 'function') {
      (window as any).gtag('event', 'conversion', {
        'send_to': 'AW-17751140940/LseUCJae5MQbEMzUs5BC',
        'event_callback': callback
      });
    } else {
      callback();
    }
  };

  const handleDemoLogin = async () => {
    setIsLoading(true);
    setError('');

    // If demo mobile and OTP are configured, use OTP-based login
    if (demoMobile && demoMobileOtp) {
      const callback = async () => {
        try {
          const result = await apiService.verifyOtp(demoMobile, demoMobileOtp);
          if (result.status) {
            // OTP verified successfully, token is already set by verifyOtp
            // Reload the page to trigger auth check and redirect to dashboard
            window.location.reload();
          } else {
            setError(result.message || 'Invalid demo OTP. Please check your environment variables.');
            setIsLoading(false);
          }
        } catch (err) {
          setError('Failed to verify demo OTP. Please try again.');
          setIsLoading(false);
        }
      };

      if (typeof (window as any).gtag === 'function') {
        if (Array.isArray((window as any).dataLayer)) {
          (window as any).dataLayer.push({
            event: "dashboard_demo_starts",
          });
        }

        (window as any).gtag('event', 'conversion', {
          'send_to': 'AW-17751140940/9Y6sCJme5MQbEMzUs5BC',
          'event_callback': callback
        });
      } else {
        await callback();
      }
      return;
    }

    // Fallback to password-based login if OTP is not configured
    if (!basicDemoAccount || !basicDemoAccount.mobile || !basicDemoAccount.password) {
      setError('Demo account not configured. Please set VITE_DEMO_MOBILE and VITE_DEMO_MOBILEOTP environment variables.');
      setIsLoading(false);
      return;
    }

    if (typeof (window as any).gtag === 'function') {
      const callback = async () => {
        const result = await login({
          mobile: basicDemoAccount.mobile,
          password: basicDemoAccount.password,
        });

        if (!result.status) {
          setError('Invalid demo credentials.');
          setIsLoading(false);
        }
      };

      if (Array.isArray((window as any).dataLayer)) {
        (window as any).dataLayer.push({
          event: "dashboard_demo_starts",
        });
      }

      (window as any).gtag('event', 'conversion', {
        'send_to': 'AW-17751140940/9Y6sCJme5MQbEMzUs5BC',
        'event_callback': callback
      });
    } else {
      const result = await login({
        mobile: basicDemoAccount.mobile,
        password: basicDemoAccount.password,
      });

      if (!result.status) {
        setError('Invalid demo credentials.');
        setIsLoading(false);
      }
    }
  };

  const verifyOtp = useCallback(async (otpValue?: string) => {
    const otpToVerify = otpValue || otp;
    if (!otpToVerify || otpToVerify.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setIsVerifyingOtp(true);
    setError('');

    try {
      const result = await apiService.verifyOtp(mobileNumber, otpToVerify);
      if (result.status) {
        // OTP verified successfully, token is already set by verifyOtp
        // Reload the page to trigger auth check and redirect to dashboard
        if (typeof (window as any).gtag === 'function') {
          (window as any).gtag('event', 'conversion', {
            'send_to': 'AW-17751140940/wUWXCJ-e5MQbEMzUs5BC'
          });
        }
        window.location.reload();
      } else {
        setError(result.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setError('Failed to verify OTP. Please try again.');
    } finally {
      setIsVerifyingOtp(false);
    }
  }, [otp, mobileNumber]);

  const startWebOtpListener = useCallback(() => {
    if (isDemoMode || typeof window === 'undefined' || !('OTPCredential' in window)) {
      return;
    }

    otpAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    otpAbortControllerRef.current = abortController;

    const request = (navigator.credentials as any)
      .get({
        otp: { transport: ['sms'] },
        signal: abortController.signal,
      } as CredentialRequestOptions)
      .then((otpCredential: any) => {
        if (otpCredential && otpCredential.code) {
          setOtp(otpCredential.code);
          setTimeout(() => {
            verifyOtp(otpCredential.code);
          }, 300);
        }
      })
      .catch((err: DOMException) => {
        if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
          console.log('OTP auto-detect error:', err);
        }
      });

    return request;
  }, [isDemoMode, verifyOtp]);

  useEffect(() => {
    return () => {
      otpAbortControllerRef.current?.abort();
    };
  }, []);

  const sendOtp = async () => {
    if (!hasAcceptedPolicies) {
      const message = 'Please review and accept the Menunix policies before requesting an OTP.';
      setPolicyError(message);
      setError(message);
      return;
    }

    if (!mobileNumber || mobileNumber.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    if (!/^[6-9]\d{9}$/.test(mobileNumber)) {
      setError('Please enter a valid 10-digit mobile number starting with 6-9');
      return;
    }

    setIsLoading(true);
    setError('');
    setPolicyError('');

    try {
      const result = await apiService.sendOtp(mobileNumber);
      if (result.status) {
        setIsOtpSent(true);
        setResendTimer(120); // Start 2-minute (120 seconds) timer
        startWebOtpListener();
      } else {
        setError(result.message || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      setError('Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return; // Prevent resend if timer is active
    await sendOtp();
  };

  // Countdown timer for resend OTP
  useEffect(() => {
    if (resendTimer <= 0) return;

    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [resendTimer]);

  // Countdown timer for reset password resend OTP
  useEffect(() => {
    if (resetResendTimer <= 0) return;

    const timer = setInterval(() => {
      setResetResendTimer((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [resetResendTimer]);

  // Auto-detect OTP using WebOTP API

  const handleSendResetOtp = async () => {
    if (!resetMobile || resetMobile.length !== 10) {
      setResetError('Please enter a valid 10-digit mobile number');
      return;
    }

    setIsResetLoading(true);
    setResetError('');

    try {
      const result = await apiService.resendOtp(resetMobile);
      if (result.status) {
        setResetStep(2);
        setResetSuccess('OTP sent to your mobile number');
        setResetResendTimer(120); // Start 2-minute (120 seconds) timer
      } else {
        setResetSuccess('');
        setResetError(result.message || 'Failed to send OTP');
      }
    } catch (err) {
      setResetError('Failed to send OTP. Please try again.');
    } finally {
      setIsResetLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetOtp || resetOtp.length !== 6) {
      setResetError('Please enter a valid 6-digit OTP');
      return;
    }

    if (!resetPassword) {
      setResetError('Password is required');
      return;
    }

    if (resetPassword.length < 8) {
      setResetError('Password must be at least 8 characters');
      return;
    }

    if (resetPassword.length > 50) {
      setResetError('Password must be less than 50 characters');
      return;
    }

    if (!/^(?=.*\d)(?=.*[@$!%*?&#]).+$/.test(resetPassword)) {
      setResetError('Password must contain at least one number, and one special character');
      return;
    }

    setIsResetLoading(true);
    setResetError('');

    try {
      const result = await apiService.resetPassword(resetMobile, resetOtp, resetPassword);
      if (result.status) {
        setResetSuccess('Password reset successfully! You can now login with your new password.');
        setTimeout(() => {
          setShowResetPassword(false);
          setResetStep(1);
          setResetMobile('');
          setResetOtp('');
          setResetPassword('');
          setResetSuccess('');
        }, 2000);
      } else {
        setResetError(result.message || 'Failed to reset password');
      }
    } catch (err) {
      setResetError('Failed to reset password. Please try again.');
    } finally {
      setIsResetLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowResetPassword(false);
    setResetStep(1);
    setResetMobile('');
    setResetOtp('');
    setResetPassword('');
    setResetError('');
    setResetSuccess('');
  };

  const renderPolicyAgreement = () => (
    <div className="pt-2">
      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={hasAcceptedPolicies}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setHasAcceptedPolicies(event.target.checked);
            if (event.target.checked) {
              setPolicyError('');
              setError('');
            }
          }}
          className="mt-1 h-5 w-5 rounded border-gray-300 text-orange-600 focus:ring-pink-600"
        />
        <span className="text-sm text-gray-600 leading-relaxed text-left">
          I have read and agree to the
          {' '}<a href="https://menunix.com/terms" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline">Menunix Terms</a>,
          {' '}<a href="https://menunix.com/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline">Privacy Policy</a>,
          {' '}<a href="https://menunix.com/cancellation-refund" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline">Cancellation &amp; Refund</a>,
          {' '}and <a href="https://menunix.com/shipping-exchange" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline">Shipping &amp; Exchange</a> policies.
        </span>
      </label>
      {policyError && (
        <p className="mt-2 text-sm text-red-600">{policyError}</p>
      )}
    </div>
  );

  return (
    <div className="h-screen bg-gradient-to-br from-orange-50 to-pink-50 flex items-center justify-center px-3 py-4 overflow-hidden">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl p-4 sm:p-6 lg:p-8 max-h-[calc(100vh-2.5rem)] overflow-y-auto sm:max-h-none sm:overflow-visible">
          <div className="flex flex-col items-center mb-6 sm:mb-8">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 to-pink-600 rounded-xl flex items-center justify-center shadow-sm">
                <span className="text-white font-bold text-2xl">M</span>
              </div>
              <span className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent">Menunix</span>
            </Link>
            <p className="mt-2 text-sm text-gray-500">Sign in to your dashboard</p>
          </div>
          {!showResetPassword ? (
            <>
              {isDemoMode && (
                <div className="mb-6 sm:mb-8">
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 sm:p-5">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-lg">ℹ️</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base sm:text-lg font-semibold text-orange-700 mb-1">Demo Mode</h3>
                        <p className="text-xs sm:text-sm text-orange-600">
                          {demoMobile && demoMobileOtp 
                            ? 'You are in demo mode. The credentials below are pre-filled and cannot be edited. Click "Sign In" to continue with the demo account dashboard.'
                            : 'You are in demo mode. You can explore the demo shop or sign in to access the demo dashboard. Note: Data entered in the demo will be cleared automatically at regular intervals.'}
                        </p>
                      </div>
                    </div>
                    {demoMobile && demoMobileOtp && (
                      <div className="mt-4 space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Mobile Number
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <PhoneIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              type="tel"
                              value={demoMobile}
                              readOnly
                              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            OTP
                          </label>
                          <input
                            type="text"
                            value={demoMobileOtp}
                            readOnly
                            className="block w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed text-center text-lg tracking-widest"
                          />
                        </div>
                      </div>
                    )}
                    <div className="mt-4 pt-4 border-t border-orange-200">
                      <a
                        href="https://demo.menunix.com"
                        onClick={handleDemoShopClick}
                        className="flex items-center justify-center gap-2 w-full bg-orange-600 text-white py-2.5 px-4 rounded-lg font-medium hover:bg-orange-700 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                      >
                        <ShoppingBagIcon className="h-5 w-5" />
                        <span>Explore Demo Shop</span>
                        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                      </a>
                    </div>
                    <div className="mt-3">
                      <button
                        onClick={handleDemoLogin}
                        disabled={isLoading}
                        className="w-full bg-orange-600 text-white py-2.5 px-4 rounded-lg font-semibold hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isLoading ? 'Signing In...' : 'Sign In to Demo Dashboard'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {!isDemoMode && (
                <>
              <div className="text-center mb-6 sm:mb-8">
                <p className="text-sm sm:text-base text-gray-600 mt-2">
                      Enter your mobile number to receive OTP
                </p>
              </div>

                  <div className="space-y-4 sm:space-y-6">
                    {!isOtpSent ? (
                      <>
                <div>
                  <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile Number
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <PhoneIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="mobile"
                      type="tel"
                              value={mobileNumber}
                              onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base"
                      placeholder="Enter your mobile number"
                      maxLength={10}
                    />
                  </div>
                          {mobileNumber.length > 0 && mobileNumber.length < 10 && (
                            <p className="mt-1 text-sm text-red-600">Mobile number must be exactly 10 digits</p>
                          )}
                          {mobileNumber.length === 10 && !/^[6-9]\d{9}$/.test(mobileNumber) && (
                            <p className="mt-1 text-sm text-red-600">Mobile number must start with 6-9</p>
                          )}
                          {mobileNumber.length === 10 && /^[6-9]\d{9}$/.test(mobileNumber) && (
                            <p className="mt-1 text-sm text-green-600">✓ Valid mobile number</p>
                          )}
                </div>

                        {renderPolicyAgreement()}

                        {error && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <p className="text-sm text-red-600">{error}</p>
                          </div>
                        )}

                        <button
                          onClick={sendOtp}
                          disabled={
                            isLoading ||
                            mobileNumber.length !== 10 ||
                            !/^[6-9]\d{9}$/.test(mobileNumber) ||
                            !hasAcceptedPolicies
                          }
                          className="w-full bg-orange-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isLoading ? 'Sending OTP...' : 'Send OTP'}
                        </button>
                      </>
                    ) : (
                      <div className="space-y-4">
                <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Enter OTP
                  </label>
                    <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            className="block w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-center text-lg tracking-widest"
                            placeholder="000000"
                            maxLength={6}
                          />
                          <p className="text-sm text-gray-500 mt-1">
                            OTP sent to +91 {mobileNumber}
                            {'OTPCredential' in window && (
                              <span className="block text-xs text-green-600 mt-1">📱 Auto-detect enabled</span>
                            )}
                          </p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                        <div className="flex space-x-3">
                <button
                            onClick={handleResendOtp}
                  disabled={isLoading || resendTimer > 0}
                            className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                            {resendTimer > 0 ? formatTimer(resendTimer) : 'Resend OTP'}
                </button>
                          <button
                            onClick={() => verifyOtp()}
                            disabled={isVerifyingOtp || otp.length !== 6}
                            className="flex-1 bg-orange-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-orange-700 disabled:opacity-50 transition-colors"
                          >
                            {isVerifyingOtp ? 'Verifying...' : 'Verify OTP'}
                          </button>
                        </div>

                        <button
                          onClick={() => {
                            setIsOtpSent(false);
                            setOtp('');
                            setError('');
                          }}
                          className="w-full text-sm text-gray-600 hover:text-gray-800"
                        >
                          ← Change mobile number
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <KeyIcon className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Reset Password</h2>
                <p className="text-gray-600 mt-2">
                  {resetStep === 1 ? 'Enter your mobile number to receive OTP' : 'Enter OTP and new password'}
                </p>
              </div>

              {resetStep === 1 ? (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mobile Number *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <PhoneIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="tel"
                        value={resetMobile}
                        onChange={(e) => setResetMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Enter 10-digit mobile number"
                        maxLength={10}
                      />
                    </div>
                  </div>

                  {resetError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-600">{resetError}</p>
                    </div>
                  )}

                  {resetSuccess && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm text-green-600">{resetSuccess}</p>
                    </div>
                  )}

                  <button
                    onClick={handleSendResetOtp}
                    disabled={isResetLoading || resetMobile.length !== 10}
                    className="w-full bg-red-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isResetLoading ? 'Sending OTP...' : 'Send OTP'}
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Enter OTP *
                    </label>
                    <input
                      type="text"
                      value={resetOtp}
                      onChange={(e) => setResetOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="block w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-center text-lg tracking-widest"
                      placeholder="000000"
                      maxLength={6}
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      OTP sent to +91 {resetMobile}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Password *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <KeyIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type={showResetPasswordField ? 'text' : 'password'}
                        value={resetPassword}
                        onChange={(e) => setResetPassword(e.target.value)}
                        className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Enter new password"
                        minLength={8}
                        maxLength={50}
                      />
                      {resetPassword && (
                        <div className="mt-2 space-y-1">
                          {resetPassword.length < 8 && (
                            <p className="text-xs text-red-600">Password must be at least 8 characters</p>
                          )}
                          {resetPassword.length > 50 && (
                            <p className="text-xs text-red-600">Password must be less than 50 characters</p>
                          )}
                          {resetPassword.length >= 8 && resetPassword.length <= 50 && !/^(?=.*\d)(?=.*[@$!%*?&#]).+$/.test(resetPassword) && (
                            <p className="text-xs text-red-600">Password must contain at least one number, and one special character</p>
                          )}
                          {resetPassword.length >= 8 && resetPassword.length <= 50 && /^(?=.*\d)(?=.*[@$!%*?&#]).+$/.test(resetPassword) && (
                            <p className="text-xs text-green-600">✓ Password meets all requirements</p>
                          )}
                        </div>
                      )}
                      <button
                        type="button"
                        aria-label={showResetPasswordField ? 'Hide password' : 'Show password'}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowResetPasswordField(!showResetPasswordField)}
                      >
                        {showResetPasswordField ? (
                          <KeyIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <KeyIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  {resetError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-600">{resetError}</p>
                    </div>
                  )}

                  {resetSuccess && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm text-green-600">{resetSuccess}</p>
                    </div>
                  )}

                  <div className="flex space-x-3">
                    <button
                      onClick={handleSendResetOtp}
                      disabled={isResetLoading || resetResendTimer > 0}
                      className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resetResendTimer > 0 ? formatTimer(resetResendTimer) : 'Resend OTP'}
                    </button>
                    <button
                      onClick={handleResetPassword}
                      disabled={isResetLoading || resetOtp.length !== 6 || resetPassword.length < 8 || resetPassword.length > 50 || !/^(?=.*\d)(?=.*[@$!%*?&#]).+$/.test(resetPassword)}
                      className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      {isResetLoading ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="text-sm text-orange-600 hover:text-orange-800 font-medium"
                >
                  ← Back to Login
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
